<template>
  <PremiumFeature feature="web_api">
    <Dialog v-model:open="open">
      <DialogTrigger as-child>
        <Button>
          <template #startIcon>
            <Plus />
          </template>
          Create new key
        </Button>
      </DialogTrigger>
      <DialogContent class="max-w-[450px]">
        <DialogHeader>
          <DialogTitle>Create a new key</DialogTitle>
        </DialogHeader>
        <div class="px-7 pb-6 pt-5 w-full md:min-w-[400px]">
          <form id="create-token" class="space-y-5" @submit="onSubmit">
            <fieldset class="flex flex-col gap-1">
              <Label for="name">Name</Label>
              <Input id="name" v-model="name" type="text" v-bind="nameAttrs" />
            </fieldset>
          </form>
        </div>
        <DialogFooter>
          <DialogClose as-child>
            <Button type="button" variant="ghost" :disabled="isPending">
              Cancel
            </Button>
          </DialogClose>
          <Button
            type="submit"
            form="create-token"
            :disabled="isPending"
            :loading="isPending"
          >
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <template #fallback>
      <UpgradeModal>
        <template #trigger>
          <Button>
            <template #startIcon>
              <Plus />
            </template>
            Create new key
          </Button>
        </template>
        <template #upgradeMessage>
          <Alert
            title="API access is available in team plans"
            subtitle="Upgrade to a team plan to unlock API access"
            class="mb-6"
          />
        </template>
      </UpgradeModal>
    </template>
  </PremiumFeature>
</template>

<script setup>
import { ref } from 'vue';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import PremiumFeature from '@/hoc/PremiumFeature.vue';
import { UpgradeModal } from '@components/common/UpgradeModal';
import { Alert } from '@ui/components/alert';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
  DialogHeader,
  DialogFooter,
  DialogClose,
} from '@ui/components/dialog';
import { useForm } from 'vee-validate';
import { z } from 'zod';
import { Plus } from 'lucide-vue-next';
import { Label } from '@ui/components/label';
import { Input } from '@ui/components/input';
import { Button } from '@ui/components/button';
import { toTypedSchema } from '@vee-validate/zod';
import requests from '@vuex/requests';
import { useStore } from 'vuex';

const validationSchema = toTypedSchema(
  z.object({
    name: z.string().min(1, { message: 'Name is required' }).max(255),
  })
);

const open = ref(false);
const queryClient = useQueryClient();

const { handleSubmit, resetForm, defineField } = useForm({
  validationSchema,
  initialValues: {
    name: '',
  },
});

const [name, nameAttrs] = defineField('name');
const store = useStore();

const { mutate, isPending } = useMutation({
  mutationFn: async nameValue => {
    await requests.workspaces.apiTokens.create({
      workspaceId: store.getters['workspace/getId'],
      name: nameValue,
    });
  },
  onSuccess: async () => {
    await queryClient.invalidateQueries({ queryKey: ['workspaceTokens'] });
    resetForm();
    open.value = false;
  },
});

const onSubmit = handleSubmit(values => {
  mutate(values.name);
});
</script>
